import { useId, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Link,
  makeStyles,
  Popover,
} from '@material-ui/core';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';
import ClearIcon from '@material-ui/icons/Clear';
import clsx from 'clsx';
import { WbBadge } from '../../../WbBadge';
import { WbCardContent } from '../../../WbCard';
import { WbNoData } from '../../../WbNoData';
import { WbChip } from '../../../WbTextField';
import { GenericEntityType, Provider, TextFieldValue } from '../../types';
import { ProviderFilters } from '../CommonFilter/types';
import { useGeneralPurposePickerContext } from '../../GeneralPurposePickerContext';

const useStyles = makeStyles(
  theme => ({
    cardContent: {
      height: '100%',
      width: '100%',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
    },
    selected: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      overflow: 'auto',
      height: '100%',
    },
    aiGenerated: {
      border: 'solid 1px transparent',
      borderRadius: '4px',
      backgroundColor: `${theme.palette.bkg.accent} !important `,
      background:
        'linear-gradient( #ffffff, #ffffff) padding-box,linear-gradient(90deg, #EE72D3, #7C4DFF) border-box',
    },
    treeChip: {
      width: 'fit-content',
      cursor: 'default',
    },
    treeChipDeleteIcon: {
      fontSize: 16,
      cursor: 'pointer',
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.text.primary,
      },
    },
    hierarchyPopover: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1),
    },
    hierarchyRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    hierarchyArrow: {
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
  }),
  { name: 'SelectedEntitiesMultiPicker' },
);

const TreeEntityChip = ({
  leaf,
  segments: initialSegments,
  entityRef,
  fetchAncestors,
  onDelete,
  className,
}: {
  leaf: string;
  segments?: string[];
  entityRef?: string;
  fetchAncestors?: (ref: string) => Promise<string[] | undefined>;
  onDelete: () => void;
  className?: string;
}) => {
  const classes = useStyles();
  const chipWrapperRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [segments, setSegments] = useState<string[] | undefined>(
    initialSegments,
  );
  const [loading, setLoading] = useState(false);
  const popoverId = useId();
  const open = Boolean(anchorEl);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // If we already have segments, just open the popover
    if (segments && segments.length > 0) {
      setAnchorEl(chipWrapperRef.current);
      return;
    }

    // Lazy load ancestors
    if (fetchAncestors && entityRef) {
      setLoading(true);
      setAnchorEl(chipWrapperRef.current);
      try {
        const result = await fetchAncestors(entityRef);
        if (result && result.length > 0) {
          setSegments(result);
        } else {
          // Fallback: just show the leaf
          setSegments([leaf]);
        }
      } catch {
        setSegments([leaf]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <div
        ref={chipWrapperRef}
        style={{ display: 'inline-flex', width: 'fit-content' }}
      >
        <WbBadge
          variant="stroke"
          color="secondary"
          className={clsx(classes.treeChip, className)}
          endIcon={
            <ClearIcon
              className={classes.treeChipDeleteIcon}
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
            />
          }
        >
          <Link
            component="button"
            variant="body2"
            underline="always"
            onClick={handleClick}
          >
            {leaf}
          </Link>
        </WbBadge>
      </div>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClick={e => e.stopPropagation()}
      >
        <Box className={classes.hierarchyPopover}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={1}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            segments?.map((segment, i) => (
              <Box
                key={`${segment}-${i}`}
                className={classes.hierarchyRow}
                style={{ paddingLeft: i * 16 }}
              >
                {i > 0 && (
                  <SubdirectoryArrowRightIcon
                    className={classes.hierarchyArrow}
                  />
                )}
                <WbBadge
                  variant="stroke"
                  color={
                    i === (segments?.length ?? 0) - 1 ? 'primary' : 'secondary'
                  }
                >
                  {segment}
                </WbBadge>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

export const SelectedEntities = ({
  entities,
  setEntities,
  selectableEntities,
  providerMapper,
}: {
  entities: TextFieldValue[];
  setEntities: (entities: TextFieldValue[]) => void;
  selectableEntities: number;
  providerMapper?: Map<string, Provider<GenericEntityType[], ProviderFilters>>;
}) => {
  const classes = useStyles();
  const handleDelete = (indexToDelete: number) => () => {
    setEntities(entities.filter((_, index) => index !== indexToDelete));
  };

  const adaptLabel = (option: TextFieldValue) => {
    const labelKind = option.kindDisplayName;
    return `${labelKind && selectableEntities > 1 ? `${labelKind}:` : ''} ${
      option.label
    }`;
  };

  const { isAiGenerated } = useGeneralPurposePickerContext();

  const isAiHighlighted = (e: TextFieldValue) => {
    return e.value && typeof e.value !== 'string' && isAiGenerated(e.value);
  };

  // Resolve the provider by the entity's OWN kind rather than the picker's
  // currently active kind tab (selectedEntityProvider). Selected entities can
  // belong to a different kind than the one currently browsed, so relying on
  // the active tab's provider would hide the hierarchy (or wrongly report no
  // hierarchy) for chips of other kinds.
  const getEntityProvider = (e: TextFieldValue) => providerMapper?.get(e.kind);

  const isTreeEntity = (e: TextFieldValue) =>
    (e.treePath && e.treePath.length > 0) ||
    !!getEntityProvider(e)?.fetchAncestors;

  const renderTreeEntity = (e: TextFieldValue, index: number) => {
    const segments = e.treePath;
    const leaf =
      segments && segments.length > 0 ? segments[segments.length - 1] : e.label;
    const entityRef =
      typeof e.value === 'string'
        ? e.value
        : (e.value?.id as string | undefined);
    const provider = getEntityProvider(e);

    return (
      <TreeEntityChip
        key={e.label}
        leaf={leaf}
        segments={segments}
        entityRef={entityRef}
        fetchAncestors={provider?.fetchAncestors?.bind(provider)}
        onDelete={handleDelete(index)}
        className={isAiHighlighted(e) ? classes.aiGenerated : ''}
      />
    );
  };

  return (
    <WbCardContent className={classes.cardContent}>
      {!entities?.length ? (
        <WbNoData
          fillContainer
          header="You haven't made any selection yet."
          text="Select the entities you need from the list on the left."
        />
      ) : (
        <Box className={classes.selected}>
          {entities?.map((e: TextFieldValue, index: number) =>
            isTreeEntity(e) ? (
              renderTreeEntity(e, index)
            ) : (
              <WbChip
                key={e.label}
                label={adaptLabel(e)}
                onDelete={handleDelete(index)}
                style={{ width: 'fit-content' }}
                className={isAiHighlighted(e) ? classes.aiGenerated : ''}
              />
            ),
          )}
        </Box>
      )}
    </WbCardContent>
  );
};
